import { Device, Call } from '@twilio/voice-sdk';
import type { MakeCallDTO } from '../types';

export interface MakeCallHandle {
    disconnect: () => void;
}

/**
 * Initiates a voice call using the provided token and destination.
 * Returns a handle to disconnect. Optional callbacks: onTokenWillExpire, onAccept, onDisconnect, onError.
 *
 * @param dto - token, to, from, params, and optional lifecycle callbacks
 * @returns Handle with disconnect()
 */
export async function makeCall(dto: MakeCallDTO): Promise<MakeCallHandle> {
    const device = new Device(dto.token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
    });

    return new Promise((resolve, reject) => {
        let hasEnded = false;
        let hasResolved = false;

        const endCall = () => {
            if (hasEnded) return;

            hasEnded = true;
            dto.onDisconnect?.();

            try {
                device.disconnectAll();
                device.destroy();
            } catch (error) {
                console.error('Error ending call', error);
            }
        };

        device.on('registering', () => dto.onRegistering?.());
        if (dto.onTokenWillExpire) {
            device.on('tokenWillExpire', dto.onTokenWillExpire);
        }

        device.on('error', (err) => {
            dto.onError?.(err.message);
            endCall();
            if (!hasResolved) reject(err);
        });
        device.on('disconnect', endCall);

        device.once('registered', () => {
            const params: Record<string, string> = {
                To: dto.to,
                from: dto.from,
            };
            if (dto.params) {
                const entries = Object.entries(dto.params);
                for (let i = 0; i < entries.length; i++) {
                    const key = entries[i][0];
                    const value = entries[i][1];
                    if (value != null) params[key] = value;
                }
            }

            device
                .connect({ params })
                .then((call) => {
                    call.on('accept', () => dto.onAccept?.());
                    call.on('disconnect', endCall);
                    call.on('cancel', endCall);
                    call.on('reject', endCall);
                    call.on('error', (err) => {
                        dto.onError?.(err.message);
                        endCall();
                    });

                    hasResolved = true;
                    resolve({
                        disconnect: () => {
                            endCall();
                        },
                    });
                })
                .catch((error) => {
                    endCall();
                    reject(error);
                });
        });

        device.register();
    });
}
